import {useEffect, useRef, useState} from 'react'
import {gsap} from 'gsap'
import Styles from './Butterfly.module.css'
import ButterflyConponent from '../../../_shared/Butterfly/Butterfly'

const Butterfly = () => {
  const butterflyRef = useRef<HTMLDivElement>(null)
  const [pearlTrigger, setPearlTrigger] = useState(false)
  const [wingIntensity, setWingIntensity] = useState(1)
  const [wingSpeedRatio, setWingSpeedRatio] = useState(1)

  const clamp = (inStart: number, inEnd: number, outStart: number, outEnd: number, inValue: number): number => {
    const value = gsap.utils.mapRange(inStart, inEnd, outStart, outEnd, inValue)
    return Math.max(Math.min(outStart, outEnd), Math.min(Math.max(outStart, outEnd), value))
  }

  useEffect(() => {
    const butterfly = butterflyRef.current
    if (butterfly) {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

      if (mediaQuery.matches) {
        gsap.set(butterfly, {opacity: 1, rotationZ: 80})
      } else {
        const startRadius = Math.PI * 0.3

        const tl = gsap.timeline({
          paused: true,
          onUpdate() {
            const _progress = tl.progress()

            setWingIntensity(() => {
              return clamp(0.3, 0.9, 2, 0, _progress)
            }) // Adjust wing intensity based on progress

            setWingSpeedRatio(() => {
              return clamp(0.4, 1.0, 1.5, 0.2, _progress)
            })

            if (_progress > 0.8) {
              setPearlTrigger(false)
            }

            const p = gsap.parseEase('power3.out')(_progress)
            const parabola = 4 * _progress * (1 - _progress)
            const radius = 400 * parabola * (1 - p)
            const x = Math.cos(p * Math.PI * 1 + startRadius) * radius * 2
            const y = Math.sin(p * Math.PI * 3 + startRadius) * radius * 0.3
            gsap.set(butterfly, {
              x: `${x}%`,
              y: `${y}%`,
            })
          },
        })

        tl.set(butterfly, {opacity: 1})
          // Y rotation
          .fromTo(
            butterfly,
            {
              rotationY: -80,
            },
            {
              rotationY: 40,
              duration: 2,
            },
            0,
          )
          .to(
            butterfly,
            {
              rotationY: 0,
              ease: 'power2.inOut',
              duration: 2,
            },
            2,
          ) // X rotation
          .fromTo(
            butterfly,
            {
              rotationX: -10,
            },
            {
              rotationX: 10,
              duration: 2,
            },
            0,
          )
          .to(
            butterfly,
            {
              rotationX: 0,
              ease: 'power2.inOut',
              duration: 2,
            },
            2,
          ) // Z roatation
          .fromTo(
            butterfly,
            {
              rotationZ: 180,
            },
            {
              rotationZ: 80 - 20,
              duration: 2,
            },
            0,
          )
          .to(
            butterfly,
            {
              rotationZ: 80,
              duration: 2,
            },
            2,
          )

        // Intersection Observer to trigger animation
        const observer = new IntersectionObserver(
          entries => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                setPearlTrigger(true)
                tl.play() // Play the animation when in viewport
                observer.unobserve(butterfly) // Stop observing after first trigger
              }
            }
          },
          {threshold: 0.4}, // Trigger when 50% of the component is visible
        )

        observer.observe(butterfly)

        return () => {
          observer.disconnect()
        }
      }

      return () => {}
    }
  }, [])

  return (
    <div className={Styles.Butterfly} ref={butterflyRef}>
      <ButterflyConponent
        DefaultColorClass={'DefaultWhiteColor'}
        pearlTrigger={pearlTrigger}
        wingIntensity={wingIntensity}
        wingSpeedRatio={wingSpeedRatio}
      />
    </div>
  )
}

export default Butterfly
